import { useEffect, useRef, useState } from "react";
import type { Coffee, CoffeePhoto } from "../types";
import { api } from "../lib/api";
import { primaryPhotoUrl, photoUrl } from "../lib/photos";

/**
 * Hero carousel: a horizontally-scrollable strip with CSS scroll-snap so
 * native swipe gestures (touch on mobile, trackpad on desktop) page through
 * photos. The hosting component tracks the current index via the scroll
 * position; tapping a thumbnail scrolls the strip to that slide.
 */
function HeroCarousel({
  coffee,
  activeIdx,
  setActiveIdx,
  scrollerRef,
}: {
  coffee: Coffee;
  activeIdx: number;
  setActiveIdx: (i: number) => void;
  scrollerRef: React.MutableRefObject<HTMLDivElement | null>;
}) {
  const photos = coffee.photos;
  const fallback = primaryPhotoUrl(coffee);

  // Detect which slide is currently centred and surface that to parent.
  // Throttled via requestAnimationFrame so we don't spam React.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || photos.length <= 1) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const w = el.clientWidth;
        if (w === 0) return;
        const i = Math.round(el.scrollLeft / w);
        if (i !== activeIdx && i >= 0 && i < photos.length) setActiveIdx(i);
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, [photos.length, activeIdx, setActiveIdx, scrollerRef]);

  if (photos.length === 0) {
    return (
      <div className="aspect-[4/5] bg-paper-100 border border-paper-200 overflow-hidden">
        {fallback ? (
          <img src={fallback} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-paper-300 text-7xl font-display">
            {coffee.variety.slice(0, 1).toUpperCase()}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <div
        ref={scrollerRef}
        className="border border-paper-200 bg-paper-100 overflow-x-auto overflow-y-hidden flex snap-x snap-mandatory"
        style={{ scrollbarWidth: "none" }}
      >
        {photos.map((p) => (
          <div key={p.id} className="snap-start shrink-0 w-full">
            <img
              src={photoUrl(p.r2_key)!}
              alt=""
              className="w-full h-auto block select-none"
              draggable={false}
            />
          </div>
        ))}
      </div>
      {photos.length > 1 ? (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 px-2 py-1 bg-paper-50/80 backdrop-blur">
          {photos.map((_, i) => (
            <span key={i} className={`block w-1.5 h-1.5 rounded-full ${i === activeIdx ? "bg-ink" : "bg-paper-300"}`} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function scrollTo(scrollerRef: React.MutableRefObject<HTMLDivElement | null>, i: number) {
  const el = scrollerRef.current;
  if (!el) return;
  el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
}

/** Read-only gallery — used for the viewer (guest) role. */
export function ReadOnlyPhoto({ coffee }: { coffee: Coffee }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const photos = coffee.photos;

  return (
    <div className="space-y-2">
      <HeroCarousel coffee={coffee} activeIdx={activeIdx} setActiveIdx={setActiveIdx} scrollerRef={scrollerRef} />
      {photos.length > 1 ? (
        <div className="grid grid-cols-5 gap-1.5">
          {photos.map((p, i) => (
            <button key={p.id} onClick={() => { setActiveIdx(i); scrollTo(scrollerRef, i); }}
              className={`aspect-square overflow-hidden border ${i === activeIdx ? "border-ink" : "border-paper-200 hover:border-paper-400"}`}>
              <img src={photoUrl(p.r2_key)!} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

interface Props {
  coffee: Coffee;
  onChange: (next: Coffee) => void;
}

export function PhotoGallery({ coffee, onChange }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  const photos = coffee.photos;
  const active = photos[activeIdx] || null;

  // Keep activeIdx in range if the photos array shrinks (e.g. after delete).
  useEffect(() => {
    if (activeIdx >= photos.length && photos.length > 0) setActiveIdx(photos.length - 1);
  }, [photos.length, activeIdx]);

  const onUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBusy(true); setError(null);
    try {
      const created: CoffeePhoto[] = [];
      for (const f of Array.from(files)) {
        created.push(await api.uploadPhoto(coffee.id, f));
      }
      onChange({ ...coffee, photos: [...coffee.photos, ...created] });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (photoId: number) => {
    if (!confirm("Remove this photo?")) return;
    setBusy(true); setError(null);
    try {
      await api.deletePhoto(coffee.id, photoId);
      const next = coffee.photos.filter((p) => p.id !== photoId);
      onChange({ ...coffee, photos: next });
      setActiveIdx((i) => Math.max(0, Math.min(i, next.length - 1)));
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const move = async (delta: -1 | 1) => {
    if (!active) return;
    const i = activeIdx;
    const j = i + delta;
    if (j < 0 || j >= photos.length) return;
    const next = [...photos];
    [next[i], next[j]] = [next[j], next[i]];
    setActiveIdx(j);
    scrollTo(scrollerRef, j);
    setBusy(true);
    try {
      const updated = await api.reorderPhotos(coffee.id, next.map((p) => p.id));
      onChange(updated);
    } catch (e) {
      setError((e as Error).message);
      onChange({ ...coffee, photos }); // revert
      setActiveIdx(i);
    } finally { setBusy(false); }
  };

  const makePrimary = async (photoId: number) => {
    const reordered = [
      ...photos.filter((p) => p.id === photoId),
      ...photos.filter((p) => p.id !== photoId),
    ];
    setBusy(true);
    try {
      const updated = await api.reorderPhotos(coffee.id, reordered.map((p) => p.id));
      onChange(updated);
      setActiveIdx(0);
      scrollTo(scrollerRef, 0);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-2">
      <HeroCarousel coffee={coffee} activeIdx={activeIdx} setActiveIdx={setActiveIdx} scrollerRef={scrollerRef} />

      {photos.length > 0 ? (
        <div className="grid grid-cols-5 gap-1.5">
          {photos.map((p, i) => (
            <button
              key={p.id}
              onClick={() => { setActiveIdx(i); scrollTo(scrollerRef, i); }}
              className={`aspect-square overflow-hidden border ${i === activeIdx ? "border-ink" : "border-paper-200 hover:border-paper-400"}`}
            >
              <img src={photoUrl(p.r2_key)!} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      ) : null}

      {photos.length > 1 && active ? (
        <div className="flex gap-1.5 text-[10px] uppercase tracking-widest">
          <button className="btn-ghost flex-1" onClick={() => move(-1)} disabled={busy || activeIdx === 0}>← Move</button>
          <button className="btn-ghost flex-1" onClick={() => move(1)} disabled={busy || activeIdx === photos.length - 1}>Move →</button>
        </div>
      ) : null}

      {active && activeIdx !== 0 ? (
        <button className="btn-ghost w-full" onClick={() => makePrimary(active.id)} disabled={busy}>
          Make primary
        </button>
      ) : null}

      <label className="btn-ghost w-full cursor-pointer">
        {busy ? "Uploading…" : photos.length > 0 ? "Add more photos" : "Upload photo"}
        <input
          type="file" accept="image/*" multiple className="hidden"
          disabled={busy}
          onChange={(e) => { onUpload(e.target.files); e.target.value = ""; }}
        />
      </label>

      {active ? (
        <button className="text-[10px] uppercase tracking-widest text-paper-400 hover:text-red-700 w-full text-left pt-1" onClick={() => onDelete(active.id)} disabled={busy}>
          Remove this photo
        </button>
      ) : null}

      {error ? <div className="text-red-700 text-xs">{error}</div> : null}
    </div>
  );
}
